/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import {
  Grid,
  Box,
  Container,
  TextField,
  useMediaQuery,
  useTheme,
  FormControlLabel,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  InputAdornment,
  IconButton,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState, useAppDispatch } from "@store/store";
import { useLazyGetLoanApplicationByIdQuery, usePostValidateNextStageMutation } from "@api/applications";
import { CustomDialog } from "@components/CustomDialog";
import { CustomSwitch } from "@components/CustomSwitch";
import { ProgressBar } from "@components/ProgressBar";
import { setModalErrorVisible } from "@store/slices/appSlice";
import {
  useCiecValidatorMutation,
  CiecValidatorRequest,
  BuroValidatorRequestPFAE,
  useBuroValidatorMutation,
} from "@api/sat_apis_new/satApsi";
import { InactivityHandler } from "@components/InactivityHandler";
import CustomCircularProgress from "@components/CustomCircularProgress";
import { Visibility, VisibilityOff, ErrorOutline } from "@mui/icons-material";
import { setCompanyData, setLoanStatus } from "@store/slices/registerSlice";
import { STATUS_CATALOG, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import { CustomTextCiecBuro } from "@components/CustomTextCiecBuro";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import useStatusConfig from "@hooks/useStatusConfig";
import useInterval from "@hooks/useInterval";
import useTofuSteps from "@hooks/useTofuSteps";
import { truncateText } from "@helpers/funciones";
import { useLazyGetPersonsQuery } from "@api/personasApi";

const SECONDS_TO_FETCH = 10;
const SCREEN_STATUS = "CSAEM";

const ConsultaCiec = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const interfaceConfig = useInterfaceConfig();
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);

  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const inputRef = useRef<HTMLInputElement>(null);

  const companyData = useSelector((state: RootState) => state.register.company);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const loanStatus = useSelector((state: RootState) => state.register.loan_status);

  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [ciecValidation] = useCiecValidatorMutation();
  const [getLoadApplication] = useLazyGetLoanApplicationByIdQuery();
  const [buroValidator] = useBuroValidatorMutation();
  const [getPersonsByLoanId] = useLazyGetPersonsQuery();

  const [isLoading, setIsLoading] = useState(false);
  const [isValidCiec, setIsValidCiec] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [closeDialog, setCloseDialog] = useState<boolean>(true);
  const [personId, setPersonId] = useState<string>("");

  const loanInterval = useInterval(async () => {
    try {
      if (!nextStatus) {
        return;
      }

      const loan_application = await getLoadApplication(application.id).unwrap();
      if (loan_application.is_correct_ciec === true) {
        loanInterval.setIsActiveInterval(false);

        await consultaBuroPfae();
        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus?.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();
        closeModal();
        dispatch(setLoanStatus(nextStatus?.code));
        dispatch(
          setCompanyData({
            ...companyData,
            is_correct_ciec: true,
          }),
        );
        navigate(nextStatus.url);
      } else if (loan_application.is_correct_ciec === false) {
        setIsValidCiec(false);
        loanInterval.setIsActiveInterval(false);
      }
    } catch (error) {
      console.error("Ha ocurrido un error al obtener la aplicación");
    }
  }, SECONDS_TO_FETCH);

  const formik = useFormik({
    initialValues: {
      valid_ciec: companyData.is_correct_ciec ? true : false,
      ciec: "",
      switch: false,
      switch2: false,
    },
    validationSchema: yup.object().shape({
      ciec: yup.string().when("valid_ciec", {
        is: (value: boolean) => value === false,
        then: (schema) => schema.required("Campo requerido").min(8, "Formato incorrecto, debe tener 8 dígitos"),
      }),
      switch: yup.boolean().when("valid_ciec", {
        is: (value: boolean) => value === false,
        then: (schema) => schema.oneOf([true], "Debe aceptar los términos y condiciones"),
      }),
      switch2: yup.boolean().when("valid_ciec", {
        is: (value: boolean) => value === false,
        then: (schema) => schema.oneOf([true], "Debe aceptar los términos y condiciones"),
      }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) return;

      try {
        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }

        if (!companyData.is_correct_ciec) {
          setSubmitting(true);

          const payload_ciec: CiecValidatorRequest = {
            rfc: companyData.rfc,
            ciec: values.ciec,
            application_code: application.company_application_code,
            client_details_id: String(application.client_details_id),
            loan_application_conditions_id: String(application.application_condition_id),
            loan_application_id: String(application.id),
            requested_amount: Number(application.loan_amount),
            requested_term: Number(application.loan_term),
          };
          await ciecValidation(payload_ciec).unwrap();

          setOpen(true);
          setIsValidCiec(null);
          loanInterval.setIsActiveInterval(true);
        } else {
          // validar si aun continua en estatus de ciec
          if (loanStatus === STATUS_CATALOG.consulta_ciec.code) {
            await triggerPostNextStage({
              applicationId: application.id,
              statusCode: nextStatus?.code,
              username: `TOFU - ${accountUser.email}`,
            }).unwrap();
            dispatch(setLoanStatus(nextStatus.code));
          }

          navigate(nextStatus.url);
        }
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "ValidacionTelefono",
            values: {},
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  const closeModal = (isError = false) => {
    setIsValidCiec(null);
    setOpen(false);
    if (isError) {
      formik.setFieldError("ciec", "La CIEC ingresada es inválida.");
      setTimeout(() => {
        inputRef.current?.focus();
      }, 500);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        const resultado = await getLoadApplication(application.id).unwrap();
        if (resultado.is_correct_ciec === true) {
          setIsValidCiec(true);
        }
        await getPersonByPfae();
      } catch (error) {
        console.error("Ha ocurrido un error al consultar el estatus de la solicitud");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const handleOnCloseDialog = () => {
    setCloseDialog(true);
  };

  useEffect(() => {
    if (formik.values.switch) {
      setCloseDialog(false);
    } else {
      setCloseDialog(true);
    }
  }, [formik.values.switch]);

  useEffect(() => {
    if (companyData.is_correct_ciec) {
      formik.setFieldValue("ciec", "12345678");
    }
  }, [companyData.is_correct_ciec]);

  const consultaBuroPfae = async () => {
    try {
      const legal_name = truncateText(companyData.name || "", 26);
      const first_name = truncateText(companyData.pfae_name || "", 26);
      const first_lastname = truncateText(companyData.pfae_last_name || "", 26);
      const second_lastname = truncateText(companyData.pfae_last_name_2 || "", 26);
      const payload_buro: BuroValidatorRequestPFAE = {
        person_Type: "pf",
        data: {
          rfc: companyData.rfc,
          transaction_number: application.id as string,
          legal_name: legal_name,
          first_name: first_name,
          second_name: "",
          father_last_name: first_lastname,
          mother_last_name: second_lastname,
          reenter_NIP: true,
          authorization_label_response: true,
          loan_application_id: application.id as string,
          is_endorsement: false,
          person_id: personId
        },
      };
      if (interfaceConfig.name === "finbe" && application.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code) {
        await buroValidator(payload_buro).unwrap();
      }
    } catch (error) {
      const apiError = error as ApiError;

      Sentry.captureException(error, {
        extra: {
          component: "ConsultaBuro",
          values: {},
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    }
  };

  const getPersonByPfae = async () => {
    try {
      if (interfaceConfig.name === "finbe" && application.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code) {
        const responseGetPersons = await getPersonsByLoanId({
          loanApplicationId: application.id,
        }).unwrap();

        if (responseGetPersons.length > 0) {
          setPersonId(responseGetPersons[0].id as string);
        }
      }
    } catch (error) {
      console.error("Ha ocurrido un error al obtener las personas de la solicitud");
    }
  };

  return (
    <>
      <Grid container height="100%">
        <Grid item xs={12} md={8} mt={2}>
          <InactivityHandler />
          <Box
            sx={{
              mt: isMobileOrTablet ? 1 : 2,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant={isMobileOrTablet ? "h4" : "h3"}
              fontWeight={500}
              textAlign="center"
              color={theme?.palette?.primary?.dark}
            >
              {interfaceConfig.textos_tofu?.pm?.consulta_ciec?.titulo || "Registro de empresa"}
            </Typography>

            <Container maxWidth="sm" sx={{ mt: 2 }}>
              <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
            </Container>

            <Box maxWidth={"sm"} mt={2} textAlign={"center"}>
              <Typography variant={isMobileOrTablet ? "body2" : "body1"} color={theme?.palette?.primary?.dark}>
                <span>
                  Haremos una consulta al SAT y al buró de crédito <br />
                  del siguiente RFC, para obtener tu oferta personalizada
                </span>
              </Typography>
            </Box>

            <Typography
              mt={2}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={600}
              color={theme?.palette?.primary?.dark}
              width={isMobileOrTablet ? "90%" : "70%"}
              textAlign={"center"}
            >
              {companyData.name}
            </Typography>

            <Typography
              mt={2}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={600}
              color={theme?.palette?.primary?.dark}
            >
              {companyData.rfc}
            </Typography>

            <Typography mt={2} variant={isMobileOrTablet ? "body2" : "body1"} color={theme?.palette?.primary?.dark}>
              Ingresa la contraseña
            </Typography>

            <Container maxWidth="sm">
              <form onSubmit={formik.handleSubmit} noValidate>
                <TextField
                  id="ciec"
                  name="ciec"
                  label="Contraseña del SAT (CIEC)"
                  type={showPassword ? "text" : "password"}
                  margin="normal"
                  required
                  fullWidth
                  autoComplete="off"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.ciec}
                  error={formik.touched.ciec && Boolean(formik.errors.ciec)}
                  helperText={formik.touched.ciec && formik.errors.ciec}
                  inputProps={{ maxLength: 8 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: theme?.palette?.primary?.main }}
                          disabled={companyData.is_correct_ciec}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  disabled={isLoading || formik.isSubmitting || companyData.is_correct_ciec}
                  inputRef={inputRef}
                />

                <Box display={"flex"} justifyContent={"center"} mt={isMobileOrTablet ? 1 : 2} textAlign="center">
                  <CustomTextCiecBuro type="textCiec" />
                </Box>

                <Box display={"flex"} justifyContent={"center"} mt={isMobileOrTablet ? 1 : 2} textAlign="center">
                  <FormControlLabel
                    control={<CustomSwitch />}
                    label={
                      <Box sx={{ paddingTop: "10px" }}>
                        <CustomTextCiecBuro type="avisoTerminosTextCiec2" />
                      </Box>
                    }
                    name="switch2"
                    checked={formik.values.switch2}
                    onChange={formik.handleChange}
                    disabled={isLoading || formik.isSubmitting}
                  />
                </Box>

                <Box display={"flex"} justifyContent={"center"} mt={isMobileOrTablet ? 1 : 2} textAlign="center">
                  <FormControlLabel
                    control={<CustomSwitch />}
                    label={
                      <Box sx={{ paddingTop: "10px" }}>
                        <CustomTextCiecBuro type="avisoTerminosTextCiec" />
                      </Box>
                    }
                    name="switch"
                    checked={formik.values.switch}
                    onChange={formik.handleChange}
                    disabled={isLoading || formik.isSubmitting}
                  />
                </Box>

                <Box display={"flex"} justifyContent={"center"} mt={2} mb={2}>
                  <Button
                    type="submit"
                    variant="default"
                    disabled={!formik.isValid || isLoading || formik.isSubmitting}
                  >
                    Continuar
                  </Button>
                </Box>
              </form>
            </Container>
          </Box>
        </Grid>

        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>

      <Dialog
        open={open}
        onClose={(_event, reason) => {
          if (reason !== "backdropClick") {
            closeModal();
          }
        }}
      >
        <DialogTitle>
          {isValidCiec === false ? (
            <Typography color={theme?.palette?.primary?.dark} textAlign={"center"} fontSize={"20px"} fontWeight={700}>
              La CIEC ingresada
              <br />
              es inválida.
            </Typography>
          ) : (
            <Typography color={theme?.palette?.primary?.dark} textAlign={"center"} fontSize={"20px"} fontWeight={700}>
              Estamos estableciendo
              <br />
              conexión con el SAT
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {isValidCiec === false ? (
            <>
              <Typography color={theme?.palette?.primary?.dark} textAlign={"center"} fontSize={"18px"} fontWeight={400}>
                Inténtalo de nuevo.
              </Typography>
              <Box mt={2} mb={2} display="flex" justifyContent="center">
                <ErrorOutline style={{ color: "red", fontSize: "40px" }} />
              </Box>

              <Box mt={2} mb={2} display="flex" justifyContent="center">
                <Button variant="default" onClick={() => closeModal(true)}>
                  Cerrar
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Box mt={2} mb={2} display="flex" justifyContent="center">
                <CustomCircularProgress size={150} />
              </Box>

              <Box mt={2} mb={2}>
                <Typography
                  color={theme?.palette?.primary?.main}
                  fontSize={"20px"}
                  fontWeight={700}
                  textAlign={"center"}
                >
                  Esto puede demorar unos
                  <br />
                  minutos
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      <CustomDialog
        colorClose={theme?.palette?.primary?.light}
        open={!closeDialog}
        onClose={handleOnCloseDialog}
        itemTitle={
          <Typography textAlign={"center"} variant="subtitle1" fontWeight={600} color={theme?.palette?.primary?.dark}>
            Consulta a Buró de crédito
          </Typography>
        }
        itemContent={
          <>
            <CustomTextCiecBuro type="modalCiec" />
          </>
        }
      />
    </>
  );
};

export default ConsultaCiec;
